import {
  BookOpen,
  Boxes,
  Building2,
  FileText,
  FolderSync,
  Gauge,
  Languages,
  Link2,
  type LucideIcon,
  MessageSquare,
  ScrollText,
  UploadCloud,
} from 'lucide-react';

import {
  adminRouters,
  IRoute,
  privateRouters,
  privateUserRouters,
  superAdminRouters,
  userRouters,
} from '../../router';

/**
 * Ordering of nav items WITHIN a group (mockup-driven). Lower index = higher up.
 * Keys not listed keep their source order, appended after listed ones.
 */
const ORDER_BY_KEY: Record<string, number> = {
  // Tools: Chat -> Summary -> Translator
  chat: 0,
  summary: 1,
  translation: 2,
  // Knowledge base: Documents -> Upload
  document: 0,
  upload: 1,
  // Administration: Connections -> Synchronization -> Usage & members
  sync: 0,
  synchronization: 1,
  'usage-members': 2,
};

/** Which group each router key belongs to (mockup IA). */
const GROUP_BY_KEY: Record<string, string> = {
  chat: 'shell.group.tools',
  summary: 'shell.group.tools',
  translation: 'shell.group.tools',
  document: 'shell.group.knowledgeBase',
  upload: 'shell.group.knowledgeBase',
  sync: 'shell.group.administration',
  synchronization: 'shell.group.administration',
  'usage-members': 'shell.group.administration',
  // Super-admin org list = mockup "Organizations" under the PLATFORM group.
  'management-system': 'shell.group.platform',
  organizations: 'shell.group.platform',
  resources: 'shell.group.help',
};

/** Group render order in the sidebar. */
const GROUP_ORDER: string[] = [
  'shell.group.tools',
  'shell.group.knowledgeBase',
  'shell.group.administration',
  'shell.group.platform',
  'shell.group.help',
];

export interface NavItem {
  key: string;
  /** route path used by react-router <Link to> */
  path: string;
  /** i18n key OR literal label (router already mixes both) */
  label: string;
  icon: LucideIcon;
  /** When set, the item is a button that dispatches this CustomEvent instead of navigating. */
  action?: string;
}

export interface NavGroup {
  /** i18n key for the group label */
  labelKey: string;
  items: NavItem[];
}

/**
 * Map a router `key` -> lucide icon. Keeps icon choices centralized.
 * Falls back to a generic icon when a key is unmapped.
 */
const ICON_BY_KEY: Record<string, LucideIcon> = {
  chat: MessageSquare,
  summary: ScrollText,
  translation: Languages,
  document: FileText,
  resources: BookOpen,
  upload: UploadCloud,
  sync: Link2,
  synchronization: FolderSync,
  'management-system': Building2,
  'usage-members': Gauge,
};

const iconFor = (key: string): LucideIcon => ICON_BY_KEY[key] ?? Boxes;

/** Only routes meant to appear in nav (mirrors legacy `isShowAllow`). */
const toNavItems = (routes: IRoute[]): NavItem[] =>
  routes
    .filter((r) => r.isShowAllow && r.label)
    .map((r) => ({
      key: r.key,
      path: r.path,
      label: r.label as string,
      icon: iconFor(r.key),
    }));

export type ShellRole = 'user' | 'admin' | 'superadmin';

const groupKeyFor = (key: string): string => GROUP_BY_KEY[key] ?? 'shell.group.tools';

/**
 * Partition a flat list of nav items into the mockup IA groups
 * (Tools / Knowledge base / Administration / Platform / Help), dedupe by key
 * (first occurrence wins), then order items within each group + order the
 * groups themselves. Routes + permissions stay byte-identical to legacy —
 * we only relabel/regroup the SAME items the role arrays already expose.
 */
const partitionGroups = (items: NavItem[]): NavGroup[] => {
  const seen = new Set<string>();
  const buckets = new Map<string, NavItem[]>();

  for (const item of items) {
    if (seen.has(item.key)) continue;
    seen.add(item.key);
    const labelKey = groupKeyFor(item.key);
    if (!buckets.has(labelKey)) buckets.set(labelKey, []);
    buckets.get(labelKey)!.push(item);
  }

  return GROUP_ORDER.filter((labelKey) => buckets.has(labelKey)).map((labelKey) => ({
    labelKey,
    items: buckets
      .get(labelKey)!
      .slice()
      .sort((a, b) => (ORDER_BY_KEY[a.key] ?? 99) - (ORDER_BY_KEY[b.key] ?? 99)),
  }));
};

/**
 * Build role-based nav groups from the SAME router arrays the app already
 * uses, so nav targets + permissions stay byte-identical to legacy behavior.
 * Grouping/labels follow the mockup IA (see GROUP_BY_KEY / GROUP_ORDER).
 */
export const buildNavGroups = (role: ShellRole): NavGroup[] => {
  if (role === 'user') {
    const userItems = [
      ...toNavItems(privateUserRouters),
      ...toNavItems(userRouters).filter((i) => !privateUserRouters.some((p) => p.key === i.key)),
    ];
    return partitionGroups(userItems);
  }

  // admin + superadmin share the private (chat/summary/...) items.
  // "Usage & members" (own-org page) lives in adminRouters, so both roles get it.
  const items = [...toNavItems(privateRouters), ...toNavItems(adminRouters)];
  if (role === 'superadmin') items.push(...toNavItems(superAdminRouters));

  return partitionGroups(items);
};
